import { Nullable } from '@dai/common'
import { withTheme } from '@emotion/react'
import styled from '@emotion/styled'
import {
  Box,
  Card as MuiCard,
  CardContent,
  Grid2 as Grid,
  Skeleton,
  Typography,
} from '@mui/material'
import { spacing } from '@mui/system'
import { ApexOptions } from 'apexcharts'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { isNil, merge } from 'lodash'
import React, { useEffect, useState } from 'react'
import Chart from 'react-apexcharts'

import { GroupByDate } from '@/graphql'
import { formatCurrency } from '@/i18n/tolgee'
import useT from '@/i18n/useT'
import { DaiAlert } from '@/pages/components/DaiAlerts'
import { ThemeProps } from '@/types/theme'
import { ChartHelpers } from '@/utils/chart.helpers'

import { getDefaultOverTimeLineChartOptions } from './ChartDefaults'

dayjs.extend(utc)

const Card = styled(MuiCard)(spacing)
const Spacer = styled.div(spacing)
const ChartWrapper = styled.div`
  width: 100%;
  position: relative;
`

export type DAIOverTimeLineChartSeries = {
  id: string
  name: string
  data: (number | null)[]
  type?: string
  color?: string
  group?: string
  hidden?: boolean
  zIndex?: number
  dataType: 'number' | 'currency' | 'percentage'
}

export interface GenericLineChartProps extends ThemeProps {
  title: string
  description?: string
  series: DAIOverTimeLineChartSeries[]
  normalize?: boolean
  dates: number[]
  options?: ApexOptions
  loading?: boolean
  type?: 'line' | 'bar'
  dataGranularity: GroupByDate
  Overlay?: React.ReactNode
}

const normalizeData = (arr: (number | null)[]) => {
  const filteredArr = arr.filter(a => a !== null) as number[]
  const min = Math.min(...filteredArr)
  const max = Math.max(...filteredArr)
  const range = max - min || 1
  return arr.map(val =>
    isNil(val) ? null : +((((val || min) - min) / range) * 100).toFixed(0),
  )
}

const formatValue = (
  value: Nullable<number>,
  dataType: 'number' | 'currency' | 'percentage',
) => {
  if (isNil(value)) {
    return 'N/A'
  }
  switch (dataType) {
    case 'number':
      return value.toLocaleString()
    case 'currency':
      return formatCurrency(value)
    case 'percentage':
      return `${value.toLocaleString()}%`
  }
}

const formatNormalizedValue = (value: Nullable<number>) => {
  if (isNil(value)) {
    return ''
  }
  return `(${value}%)`
}

const DAIOverTimeLineChart: React.FC<GenericLineChartProps> = ({
  theme,
  title,
  description,
  series: _series,
  normalize,
  dates,
  options,
  loading,
  type = 'line',
  dataGranularity,
  Overlay,
}) => {
  const { t } = useT()
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 800,
  )

  useEffect(() => {
    let timeoutId: NodeJS.Timeout
    const handleResize = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        setWindowWidth(window.innerWidth)
      }, 100)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      clearTimeout(timeoutId)
    }
  }, [])

  const series: (DAIOverTimeLineChartSeries & {
    rawData: (number | null)[]
  })[] = _series?.map(s => ({
    id: s.id,
    name: s.name,
    data:
      normalize && s.dataType !== 'percentage' ? normalizeData(s.data) : s.data,
    rawData: s.data,
    color: s.color,
    dataType: s.dataType,
  }))

  const numberOfIncompletePeriods = ChartHelpers.getNumberOfIncompletePeriods(
    dates.map(date => dayjs(date)),
    dataGranularity,
  )

  const getEnhancedOptions = (
    series: (DAIOverTimeLineChartSeries & {
      rawData: (number | null)[]
    })[],
  ): ApexOptions => {
    const isMobile = windowWidth < 900
    const defaultOptions = getDefaultOverTimeLineChartOptions(
      theme,
      title,
      t,
      dataGranularity,
      series,
      dates,
      loading,
      isMobile,
    )

    const baseOptions = merge({}, defaultOptions, options, {
      chart: {
        id: title,
        animations: {
          enabled: numberOfIncompletePeriods === 0,
        },
      },
    })

    const completionPercentage = ChartHelpers.getCompletionPercentage(
      dates.map(date => dayjs(date)),
      dataGranularity,
    )

    return {
      ...baseOptions,
      dataLabels: {
        enabled: false,
      },
      chart: {
        ...baseOptions.chart,
      },
      tooltip: {
        ...baseOptions.tooltip,
        custom: ({ dataPointIndex, w }) => {
          const categoryLabel = dates?.[dataPointIndex]
            ? dayjs
                .utc(dates[dataPointIndex])
                .format(
                  ChartHelpers.getLongDateFormatFromGroupBy(dataGranularity),
                )
            : ''
          const hasIncompleteData =
            dataPointIndex >= dates.length - numberOfIncompletePeriods &&
            numberOfIncompletePeriods > 0

          const rows: { value: number; html: string }[] = []

          w.config.series.forEach((seriesConfig: any, i: number) => {
            const rawData = series?.find(s => s.id === seriesConfig.id)?.rawData
            const value = rawData?.[dataPointIndex]
            const normVal = seriesConfig?.data[dataPointIndex]

            rows.push({
              value: normalize ? normVal : value,
              html: `
                <div style="margin-bottom: 5px; display: flex; align-items: center; gap: 8px;">
                  <div style="width: 12px; height: 12px; background-color: ${w.globals.colors[i]}; border-radius: 2px;"></div>
                  <span>${seriesConfig.name}: <strong>${formatValue(value, seriesConfig.dataType)}</strong> ${normalize ? `<span>${formatNormalizedValue(normVal)}</span>` : ''}</span>
                </div>
              `,
            })
          })

          const tooltip = `
            <div style="padding: 12px; min-width: 180px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">
              <div style="font-weight: bold; margin-bottom: 8px; color: ${theme.palette.text.primary};">${categoryLabel}</div>
              ${rows
                .sort((a, b) => b.value - a.value)
                .map(row => row.html)
                .join('')}
              ${
                hasIncompleteData
                  ? `
                <div style="margin-top: 12px; padding: 8px; background-color: #d1ecf1; border-radius: 6px; font-size: 12px; color: #0c5460; border-left: 3px solid #17a2b8;">
                  <div style="font-weight: bold; margin-bottom: 4px; display: flex; align-items: center; gap: 4px;">
                    ${t('daiLineChart.tooltip.inProgress', { granularity: dataGranularity.toLowerCase() })}
                  </div>
                  <div style="display: flex; align-items: center; gap: 8px; margin-top: 6px;">
                    <div style="flex: 1; background-color: #e9ecef; border-radius: 10px; height: 6px; overflow: hidden;">
                      <div style="background-color: #17a2b8; height: 100%; border-radius: 10px; width: ${completionPercentage}%; transition: width 0.3s ease;"></div>
                    </div>
                    <span style="font-size: 11px; font-weight: 600; min-width: 30px;">${completionPercentage}%</span>
                  </div>
                </div>
              `
                  : ''
              }
            </div>
          `
          return tooltip
        },
      },
    }
  }

  const chartOptions = getEnhancedOptions(series)

  return (
    <Card mb={1}>
      {loading ? (
        <CardContent>
          <Skeleton sx={{ height: 400 }} />
        </CardContent>
      ) : (
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" gutterBottom>
              {description}
            </Typography>
          )}
          <Spacer mb={6} />
          <ChartWrapper>
            {series && series.length > 0 && dates && dates.length > 0 ? (
              <Chart
                key={title}
                options={chartOptions}
                series={series}
                type={type}
                height={chartOptions.chart?.height ?? '350'}
              />
            ) : Overlay ? (
              <Box sx={{ height: 150 }} />
            ) : (
              <Grid
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <DaiAlert message={t('chartDefaults.noData')} />
              </Grid>
            )}
            {Overlay && (
              <Box
                sx={{
                  position: 'absolute',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backdropFilter: 'blur(5px)',
                }}
              >
                {Overlay}
              </Box>
            )}
          </ChartWrapper>
        </CardContent>
      )}
    </Card>
  )
}

export default withTheme(DAIOverTimeLineChart)
